import { batch, Signal, signal } from '@preact/signals'

import {
  BarcodeCapture,
  BarcodeCaptureFeedback,
  BarcodeCaptureSettings,
  BarcodeBatch,
  BarcodeBatchSettings,
  Symbology,
} from '@scandit/web-datacapture-barcode'
import { BrowserHelper, DataCaptureMode, Feedback } from '@scandit/web-datacapture-core'

import { SymbologyOption, SymbologyOptionExtension } from './SymbologyOption'
import modeSymbologies, { ExtensionName, symbologiesExtension } from './modeSymbologies'

import { defaultRangeBySymbology, extendedRangeBySymbology } from './rangeSymbologies'
import { ScannerMode } from './InitialOptionsStore'
import { parseSymbologiesFromURL } from './parseSymbologiesFromURL'

export type SettingName = 'soundEffects' | 'duplicateFilter' | 'extendedSymbolCount' | 'colorInvertedEnabled'

export type MenuSettingName = 'soundEffects' | 'duplicateFilter'

export abstract class SettingsStore {
  public colorInverted: Signal<boolean> = signal(false)
  public extendedSymbolCount: Signal<boolean> = signal(false)
  public soundEffects: Signal<boolean> = signal(false)
  public duplicateFilter: Signal<boolean> = signal(true)
  public allSymbologiesOptionsMap: Record<Symbology, SymbologyOption>
  public symbologiesAddonsOptions: SymbologyOptionExtension[]

  public mode: BarcodeCapture | BarcodeBatch
  public settings: BarcodeBatchSettings | BarcodeCaptureSettings
  public symbologiesOptions: SymbologyOption[]
  private symbologiesAddonsOptionsMap: Record<string, SymbologyOptionExtension>

  constructor(
    options:
      | { mode: BarcodeCapture; settings: BarcodeCaptureSettings }
      | { mode: BarcodeBatch; settings: BarcodeBatchSettings }
  ) {
    this.allSymbologiesOptionsMap = modeSymbologies.all.reduce(
      (acc, symbology) => {
        acc[symbology] = new SymbologyOption(symbology)
        return acc
      },
      {} as Record<Symbology, SymbologyOption>
    )
    this.symbologiesAddonsOptionsMap = symbologiesExtension.all.reduce(
      (acc, addon) => {
        acc[addon.name] = new SymbologyOptionExtension(addon)
        return acc
      },
      {} as Record<string, SymbologyOptionExtension>
    )

    this.symbologiesOptions = Object.values(this.allSymbologiesOptionsMap)
    this.symbologiesAddonsOptions = Object.values(this.symbologiesAddonsOptionsMap)
    this.mode = options.mode
    this.settings = options.settings
  }

  async apply() {
    // @ts-expect-error 2345
    await this.mode.applySettings(this.settings)
  }

  activateSymbologies(symbologies: Symbology[]): SettingsStore {
    batch(() => {
      for (const symbology of symbologies) {
        this.settings.enableSymbology(symbology, this.allSymbologiesOptionsMap[symbology].activate())
      }
    })
    return this
  }

  init(scannerMode: ScannerMode): SettingsStore {
    const { symbologies, extensions } = parseSymbologiesFromURL()
    const extensionsNameToToggle = symbologiesExtension['1dRetail'].map(({ name }) => name)
    const symbologiesToActivate = modeSymbologies[scannerMode]

    const isIosOrMacOS = ['mac os', 'ios'].includes((BrowserHelper.getUserAgentInfo().getOS().name ?? '').toLowerCase())

    // save the default ones
    for (const [symbology] of Object.entries(defaultRangeBySymbology)) {
      defaultRangeBySymbology[symbology as Symbology] = this.settings
        .settingsForSymbology(symbology as Symbology)
        .activeSymbolCounts.slice(1)
    }

    this.setSoundEffects(!isIosOrMacOS)
    this.activateSymbologies([...new Set(symbologiesToActivate.concat(symbologies))])
      // We want to disable extensions when the preset 1dRetail is unselected
      .setExtensions(extensionsNameToToggle, scannerMode === ScannerMode.OneDRetail)
      // if ?dpm querystring is present there will be direct_part_marking_mode extension to be activated
      .setExtensions(extensions as ExtensionName[], true)
      .setExtendSymbolsCount(false)
      .setDuplicateFilter(true)

    return this
  }

  setColorInverted(bool: boolean) {
    for (const option of Object.values(this.allSymbologiesOptionsMap)) {
      const symbologySetting = this.settings.settingsForSymbology(option.symbology)
      symbologySetting.isColorInvertedEnabled = bool
    }
    this.colorInverted.value = bool
    return this
  }

  setExtendSymbolsCount(bool: boolean) {
    this.extendedSymbolCount.value = bool

    for (const [symbology, range] of Object.entries(
      this.extendedSymbolCount.value ? extendedRangeBySymbology : defaultRangeBySymbology
    )) {
      this.settings.settingsForSymbology(symbology as Symbology).activeSymbolCounts = range
    }

    return this
  }

  setMaxCodePerFrame(enable: boolean, codesPerFrame = 4) {
    this.settings.setProperty('max_codes_per_frame', enable ? codesPerFrame : 0)
    return this
  }

  toggleSymbology(symbology: Symbology): SettingsStore {
    const option = this.allSymbologiesOptionsMap[symbology]
    this.settings.enableSymbology(symbology, option.toggle())
    return this
  }

  activateSymbology(symbology: Symbology): SettingsStore {
    this.settings!.enableSymbology(symbology, this.allSymbologiesOptionsMap[symbology].activate())
    return this
  }

  deactivateAllSymbologies(options?: { exclusions?: Symbology[] }) {
    batch(() => {
      Object.values(this.allSymbologiesOptionsMap).forEach((option) => {
        if (options?.exclusions?.includes(option.symbology)) return
        this.settings.enableSymbology(option.symbology, option.deactivate())
      })
    })
    return this
  }
  /**
   * Enables the extensions for the symbologies that supports it
   */
  setExtensions(extensions: string[], enabled: boolean): SettingsStore {
    for (const symbologyOption of Object.values(this.allSymbologiesOptionsMap)) {
      extensions
        .filter((extension) => symbologyOption.description.supportedExtensions.includes(extension))
        .forEach((toActivate) => {
          this.settings!.settingsForSymbology(symbologyOption.symbology).setExtensionEnabled(toActivate, enabled)
          // for the direct_part_marking_mode we don't have a switch option view
          // but the extension could be enabled through querystring with the ?dpm param
          if (this.symbologiesAddonsOptionsMap[toActivate])
            this.symbologiesAddonsOptionsMap[toActivate].checked.value = enabled
        })
    }

    return this
  }

  abstract setSoundEffects(bool: boolean): Promise<SettingsStore>
  abstract setDuplicateFilter(bool: boolean): SettingsStore
}

const barcodeCaptureFeedbackDisabled = new BarcodeCaptureFeedback()
barcodeCaptureFeedbackDisabled.success = new Feedback(null, null)
export class BarcodeCaptureSettingsStore extends SettingsStore {
  async setSoundEffects(bool: boolean): Promise<SettingsStore> {
    this.soundEffects.value = bool
    await (this.mode as BarcodeCapture).setFeedback(
      this.soundEffects.value ? BarcodeCaptureFeedback.default : barcodeCaptureFeedbackDisabled
    )
    return this
  }

  setDuplicateFilter(bool: boolean): SettingsStore {
    this.duplicateFilter.value = bool
    ;(this.settings as BarcodeCaptureSettings).codeDuplicateFilter = this.duplicateFilter.value ? 800 : 0
    return this
  }
}

export class BarcodeBatchSettingsStore extends SettingsStore {
  public duplicateFilter: Signal<boolean> = signal(false)
  async setSoundEffects(bool: boolean): Promise<SettingsStore> {
    // todo:
    return this
  }

  setDuplicateFilter(bool: boolean): SettingsStore {
    // nothing to do here
    return this
  }
}
